/**
 * Client Supabase - Configuration et utilitaires d'authentification
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    'Variables d\'environnement Supabase manquantes. Vérifiez votre fichier .env'
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Récupère la session utilisateur courante
 * @returns {Promise<{data: {session: object}, error: object}>}
 */
export async function getSession() {
  return await supabase.auth.getSession();
}

/**
 * Récupère l'utilisateur courant
 * @returns {Promise<object|null>}
 */
export async function currentUser() {
  const { data, error } = await getSession();
  if (error) {
    console.error('Erreur lors de la récupération de l\'utilisateur:', error);
    return null;
  }
  return data?.session?.user || null;
}

/**
 * Connexion avec email et mot de passe
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{data: object, error: object}>}
 */
export async function signInWithEmail(email, password) {
  return await supabase.auth.signInWithPassword({ email, password });
}

/**
 * Inscription avec email et mot de passe
 * @param {string} email
 * @param {string} password
 * @param {object} metadata - Métadonnées utilisateur (nom, etc.)
 * @returns {Promise<{data: object, error: object}>}
 */
export async function signUpWithEmail(email, password, metadata = {}) {
  return await supabase.auth.signUp({
    email,
    password,
    options: {
      data: metadata,
      emailRedirectTo: `${window.location.origin}/`,
    },
  });
}

/**
 * Connexion avec magic link
 * @param {string} email
 * @returns {Promise<{data: object, error: object}>}
 */
export async function signInWithMagicLink(email, { redirect } = {}) {
  // Récupérer l'établissement_id actuel pour le transmettre dans le Magic Link
  const etablissementId = localStorage.getItem('etablissement_id');

  // Construire l'URL de redirection avec la cible dans la query AVANT le hash
  // Nouveau format: https://host/#/auth/callback?redirect=/[&etablissement=ID]
  const params = new URLSearchParams();
  if (redirect) params.set('redirect', redirect);
  if (etablissementId) {
    params.set('etablissement', etablissementId);
    console.warn('🔗 Magic Link redirigera vers établissement:', etablissementId);
  }
  const qs = params.toString();
  let redirectUrl = `${window.location.origin}/#/auth/callback`;
  if (qs) redirectUrl += `?${qs}`;

  return await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: redirectUrl,
    },
  });
}

/**
 * Déconnexion
 * @returns {Promise<{error: object}>}
 */
export async function signOut() {
  try {
    const storageKey =
      supabase?.auth?.storageKey ||
      `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;

    const clearStorage = () => {
      try {
        localStorage.removeItem(storageKey);
        localStorage.removeItem(`${storageKey}-code-verifier`);
        localStorage.removeItem(`${storageKey}-user`);
      } catch {}
      try {
        sessionStorage.removeItem(storageKey);
        sessionStorage.removeItem(`${storageKey}-code-verifier`);
        sessionStorage.removeItem(`${storageKey}-user`);
      } catch {}
    };

    const { data } = await supabase.auth.getSession();
    const session = data?.session || null;

    const expiresAtMs = session?.expires_at ? session.expires_at * 1000 : null;
    const isExpired = expiresAtMs ? Date.now() >= expiresAtMs : false;

    const hasTokens = !!session?.access_token && !!session?.refresh_token;

    if (!session || !hasTokens || isExpired) {
      clearStorage();
      return { error: null };
    }

    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        clearStorage();
        return { error: null };
      }
    } catch {
      clearStorage();
      return { error: null };
    }

    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) {
      clearStorage();
      return { error: null };
    }

    return { error: null };
  } catch {
    try {
      const storageKey =
        supabase?.auth?.storageKey ||
        `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
      try {
        localStorage.removeItem(storageKey);
        localStorage.removeItem(`${storageKey}-code-verifier`);
        localStorage.removeItem(`${storageKey}-user`);
      } catch {}
      try {
        sessionStorage.removeItem(storageKey);
        sessionStorage.removeItem(`${storageKey}-code-verifier`);
        sessionStorage.removeItem(`${storageKey}-user`);
      } catch {}
    } catch {}
    return { error: null };
  }
}

/**
 * Écoute les changements d'authentification
 * @param {Function} callback - Fonction appelée lors des changements
 * @returns {Function} - Fonction de nettoyage
 */
export function onAuthStateChange(callback) {
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });
  return () => data?.subscription?.unsubscribe();
}

/**
 * Récupère l'établissement_id d'un client depuis la table clients
 * @param {string} userId - ID de l'utilisateur auth
 * @returns {Promise<{data: string|null, error: object}>}
 */
export async function getClientEtablissement(_userId) {
  const { data: authInfo } = await supabase.auth.getUser();
  const email = (authInfo?.user?.email || '').trim();
  if (!email) {
    return { data: null, error: null };
  }

  const { data, error } = await supabase
    .from('clients')
    .select('etablissement_id')
    .ilike('email', email)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Erreur récupération établissement client:', error);
    return { data: null, error };
  }

  return { data: data?.etablissement_id || null, error: null };
}

/**
 * Crée ou récupère le profil client lié à l'utilisateur (WebEdena)
 * @param {object} user - Utilisateur Supabase auth
 * @param {string} etablissementId - ID de l'établissement
 * @returns {Promise<{data: object, error: object}>}
 */
export async function getOrCreateClientProfile(user, etablissementId) {
  const email = (user?.email || '').trim();
  if (!email) {
    return { data: null, error: null };
  }

  // Tenter de retrouver un client par (etablissement_id, email)
  const { data: byEmail, error: emailError } = await supabase
    .from('clients')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .ilike('email', email)
    .maybeSingle();

  if (emailError) {
    console.error('Erreur fetch client par email:', emailError);
    return { data: null, error: emailError };
  }

  if (byEmail) {
    return { data: byEmail, error: null };
  }

  // Créer le profil client
  // Ne pas utiliser le préfixe de l'email comme prénom par défaut
  const fullName = user.user_metadata?.full_name || '';
  const [prenom = '', nom = ''] = fullName ? fullName.split(' ') : ['', ''];

  const { data, error } = await supabase
    .from('clients')
    .upsert(
      [
        {
          etablissement_id: etablissementId,
          utilisateur_id: null,
          email,
          prenom: user.user_metadata?.prenom || prenom || null,
          nom: user.user_metadata?.nom || nom || null,
          telephone: user.user_metadata?.telephone || null,
        },
      ],
      { onConflict: 'etablissement_id,email' }
    )
    .select()
    .single();

  return { data, error };
}
